import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import mapboxgl from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getLang } from "../../../shared/tools";
import style from "./style.module.scss";
import "mapbox-gl/dist/mapbox-gl.css";
import crossIcon from "./cross-icon.svg";
import Filters from "./filters";

const Search = ({ map, centerAndResize, countryCode }) => {
  const geocoderRef = useRef(null);
  const geolocRef = useRef(null);
  const inputRef = useRef(null);
  const [showClearButton, setShowClearButton] = useState(false);
  const { t } = useTranslation();
  const language = getLang();
  const [featureGeocoder, setFeatureGeocoder] = useState(null);
  const [radius, setRadius] = useState(0);

  const geocoder = useMemo(() => {
    return new MapboxGeocoder({
      accessToken: mapboxgl.accessToken ?? "",
      placeholder: t("Address, postal code, city") + "...",
      flyTo: {
        maxZoom: 12,
      },
      marker: false,
      language: language,
      countries: countryCode ?? undefined,
    });
  }, [language, t, countryCode]);

  const geolocate = useMemo(() => {
    return new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: false,
      showUserHeading: false,
      fitBoundsOptions: {
        maxZoom: 12,
        duration: 3000,
      },
    });
  }, []);

  useEffect(() => {
    if (geocoderRef.current && geocoderRef.current.children.length > 0) return;

    if (geocoder && geocoderRef.current && map) {
      const handleInput = () => {
        if (inputRef.current && inputRef.current.value !== "") {
          setShowClearButton(true);
        } else {
          setShowClearButton(false);
        }
      };

      geocoder.on("result", (e) => {
        const placeName = e.result?.place_name || "";
        if (inputRef.current) {
          inputRef.current.value = placeName;
        }
        setShowClearButton(true);
        centerAndResize(e);

        setFeatureGeocoder(e.result);
      });

      geolocate.on("geolocate", centerAndResize);

      const geocoderControl = geocoder.onAdd(map);
      geocoderRef.current.textContent = "";
      geocoderRef.current.appendChild(geocoderControl);

      inputRef.current = geocoderRef.current.querySelector(
        ".mapboxgl-ctrl-geocoder--input"
      );

      inputRef.current.addEventListener("input", handleInput);

      geolocRef.current.appendChild(geolocate.onAdd(map));

      return () => {
        if (inputRef.current) {
          inputRef.current.removeEventListener("input", handleInput);
        }
      };
    }
  }, [geocoder, geocoderRef, map, centerAndResize, geolocRef, geolocate]);

  const clearInput = () => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setShowClearButton(false);
      geocoder.clear();
    }
  };

  return (
    <>
      <div className={style.searchResellerContainer}>
        <div className={style.searchResellerGeocoder} ref={geocoderRef}>
          {showClearButton && (
            <button className={style.clearButton} onClick={clearInput}>
              <img src={crossIcon} alt="" aria-hidden="true" />
            </button>
          )}
        </div>
        <div className={style.searchResellerGeoloc} ref={geolocRef} />
      </div>

      <Filters
        map={map}
        featureGeocoder={featureGeocoder}
        radius={radius}
        setRadius={setRadius}
      />
    </>
  );
};

export default Search;
